'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

import { Button, Typography } from 'lighthouse';

import AttributeFiltersView from '@/widgets/AttributeFilters/AttributeFiltersView';
import CatalogProductCard from '@/widgets/CatalogProductCard';
import { catalogItemFromOffer } from '@/widgets/CatalogProductCard/mappers';
import CatalogSkeleton from '@/widgets/ServiceLanding/CatalogSkeleton';
import Reveal from '@/widgets/ServiceLanding/Reveal';
import SectionHeading from '@/widgets/ServiceLanding/SectionHeading';

import { useCategoryAttributeFilters } from '@/hooks/filters/useCategoryAttributeFilters';

import { ATTRIBUTE_FILTER_COPY } from '@/utilities/attributeFilters/constants';

import { useCityStore } from '@/stores/city/useCityStore';

import { PRODUCTS_PAGE_SIZE, PRODUCTS_SECTION_ID, productsCopy } from '@/constants/brandLanding';
import { TEHRAN_CITY_ID } from '@/constants/common';
import { DEFAULT_PRODUCT_OFFER_SORT_BY } from '@/constants/productSort';

import BrandCategoryFilter from '../BrandCategoryFilter';
import { BrandProductsCatalogProps, ProductsStatus } from './models';

import { getFilteredProductsService } from '@/client/services/vendor/methods';
import { ProductType } from '@/server/services/core/product/models';
import { OfferContent, ProductOfferSortBy } from '@/server/services/vendor/models';
import { Box1, Refresh, Shop } from 'iconsax-react';

const mapProductTypeToOffer = (product: ProductType): OfferContent => ({
    productUuid: product.uuid,
    productTitle: product.title,
    productSlug: product.slug,
    brandName: product.brand?.name,
    categoryId: product.category?.id,
    price: product.price,
    sellingPrice: product.sellingPrice,
    discountPercentage: product.discountPercentage,
    availableQuantity: product.available ? 1 : 0,
    guarantee: undefined
});

const BrandProductsCatalog = ({ brandId, brandName, initialProducts, initialIsLast, categories }: BrandProductsCatalogProps) => {
    const searchParams = useSearchParams();
    const cityId = useCityStore((state) => state.cityId);
    const categoryParam = searchParams.get('categoryId');
    const categoryId = categoryParam != null ? Number(categoryParam) : null;
    const activeCategoryId = Number.isFinite(categoryId as number) ? categoryId : null;
    const activeCategoryName = categories.find((category) => category.id === activeCategoryId)?.name;
    const attributeFilters = useCategoryAttributeFilters({ categoryId: activeCategoryId });
    const attributeFilterKey = attributeFilters.attributeFilterParams.join('|');
    const [sortBy, setSortBy] = useState<ProductOfferSortBy>(DEFAULT_PRODUCT_OFFER_SORT_BY);

    const [products, setProducts] = useState<OfferContent[]>(initialProducts.map(mapProductTypeToOffer));
    const [page, setPage] = useState(0);
    const [isLastPage, setIsLastPage] = useState(initialIsLast);
    const [status, setStatus] = useState<ProductsStatus>('loading');
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const visibleProducts = products.filter((product) => product.discountPercentage !== 99);

    const fetchProducts = async (nextCityId: number, nextCategoryId: number | null, nextSortBy = sortBy) => {
        setStatus('loading');
        setPage(0);
        try {
            const response = await getFilteredProductsService({
                brandIds: [brandId],
                categoryIds: nextCategoryId != null ? [nextCategoryId] : undefined,
                cityId: nextCityId,
                page: 0,
                size: PRODUCTS_PAGE_SIZE,
                sortBy: nextSortBy,
                ...(attributeFilters.attributeFilterParams.length ? { attributeFilter: attributeFilters.attributeFilterParams } : {})
            });
            setProducts(response.data.content);
            setIsLastPage(response.data.last);
            setStatus('success');
        } catch {
            setProducts([]);
            setIsLastPage(true);
            setStatus('error');
        }
    };

    const handleLoadMore = async () => {
        if (isLoadingMore || isLastPage) return;

        setIsLoadingMore(true);
        const nextPage = page + 1;

        try {
            const response = await getFilteredProductsService({
                brandIds: [brandId],
                categoryIds: activeCategoryId != null ? [activeCategoryId] : undefined,
                cityId: cityId || TEHRAN_CITY_ID,
                page: nextPage,
                size: PRODUCTS_PAGE_SIZE,
                sortBy: sortBy,
                ...(attributeFilters.attributeFilterParams.length ? { attributeFilter: attributeFilters.attributeFilterParams } : {})
            });
            setProducts((prev) => [...prev, ...response.data.content]);
            setIsLastPage(response.data.last);
            setPage(nextPage);
        } catch {
            setIsLastPage(true);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        if (attributeFilters.isLoading) {
            return;
        }

        void fetchProducts(cityId || TEHRAN_CITY_ID, activeCategoryId, sortBy);
    }, [brandId, cityId, activeCategoryId, attributeFilterKey, attributeFilters.isLoading, sortBy]);

    return (
        <section id={PRODUCTS_SECTION_ID} className='scroll-mt-4 bg-gradient-to-b from-sky-50/50 via-white to-white py-16 md:scroll-mt-6 md:py-24'>
            <div className='desktop-width px-4'>
                <SectionHeading
                    badge={productsCopy.badge}
                    title={activeCategoryName ? `${brandName} · ${activeCategoryName}` : `محصولات ${brandName}`}
                    subtitle={productsCopy.subtitle}
                />

                <div
                    className='sticky top-0 z-30 -mx-4 mb-4 space-y-3 bg-white/95 px-4 py-3 backdrop-blur-md md:top-16 md:mx-0 md:rounded-lg md:border
                        md:border-N100 md:px-4 md:shadow-sm'>
                    <div className='flex items-center gap-2'>
                        <span className='flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary'>
                            <Shop size={15} color='currentColor' variant='Bulk' />
                        </span>
                        <Typography variant='subtitle2' component='h3' color='blackHighEmphasis' className='font-bold'>
                            {activeCategoryName || productsCopy.allCategories}
                        </Typography>
                    </div>
                    <BrandCategoryFilter categories={categories} activeCategoryId={activeCategoryId} />
                </div>

                <AttributeFiltersView filters={attributeFilters} sortBy={sortBy} onSortChange={setSortBy}>
                    <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                        {status === 'loading' && <CatalogSkeleton />}

                        {status === 'error' && (
                            <div className='col-span-full flex flex-col items-center gap-4 rounded-lg border border-dashed border-N200 bg-white py-16'>
                                <Typography variant='body2' color='blackMediumEmphasis' component='p'>
                                    {productsCopy.errorTitle}
                                </Typography>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    size='medium'
                                    startIcon={<Refresh size={18} color='currentColor' />}
                                    className='cursor-pointer'
                                    onClick={() => void fetchProducts(cityId || TEHRAN_CITY_ID, activeCategoryId)}>
                                    تلاش مجدد
                                </Button>
                            </div>
                        )}

                        {status === 'success' &&
                            visibleProducts.map((product, index) => (
                                <Reveal key={product.productUuid} delay={Math.min(index, 7) * 40} className='h-full'>
                                    <CatalogProductCard item={catalogItemFromOffer(product)} showSelectButton={false} />
                                </Reveal>
                            ))}

                        {status === 'success' && !visibleProducts.length && (
                            <div className='col-span-full flex flex-col items-center gap-3 rounded-lg border border-dashed border-N200 bg-white py-16'>
                                <span className='flex h-14 w-14 items-center justify-center rounded-lg bg-sky-50 text-primary'>
                                    <Box1 size={28} color='currentColor' variant='Bulk' />
                                </span>
                                <Typography variant='subtitle2' component='p' color='blackHighEmphasis' className='font-bold'>
                                    {attributeFilters.activeCount ? ATTRIBUTE_FILTER_COPY.emptyResults : productsCopy.emptyTitle}
                                </Typography>
                                <Typography variant='caption' component='p' color='blackMediumEmphasis'>
                                    {productsCopy.emptySubtitle}
                                </Typography>
                            </div>
                        )}
                    </div>

                    {status === 'success' && !!visibleProducts.length && !isLastPage && (
                        <div className='mt-8 flex justify-center'>
                            <Button
                                variant='outlined'
                                color='primary'
                                size='large'
                                loading={isLoadingMore}
                                onClick={() => void handleLoadMore()}
                                className='min-w-[220px] cursor-pointer'>
                                {productsCopy.loadMore}
                            </Button>
                        </div>
                    )}
                </AttributeFiltersView>
            </div>
        </section>
    );
};

export default BrandProductsCatalog;
